import React, { useEffect, useState } from 'react';
import { Activity, Goal, Users, CheckCircle2, Clock, RefreshCw, Banknote } from 'lucide-react';
import { api } from './api';

const CUR = { gbp: '£', eur: '€', usd: '$' };

const Tile = ({ icon, label, value, accent = '#00BFFF' }) => (
  <div className="bg-[#1C1C1E] border border-gray-800 rounded-2xl p-4">
    <div className="flex items-center gap-2 mb-2" style={{ color: accent }}>{icon}<span className="text-[10px] text-gray-500 uppercase font-bold tracking-widest">{label}</span></div>
    <p className="text-3xl font-black">{value}</p>
  </div>
);

const eventDot = (type) => type === 'goal' ? <span className="w-2.5 h-2.5 rounded-full bg-white inline-block" />
  : type === 'yellow' ? <span className="w-2.5 h-3.5 rounded-sm bg-[#FFD400] inline-block" />
  : type === 'red' ? <span className="w-2.5 h-3.5 rounded-sm bg-[#FF3B30] inline-block" />
  : <span className="w-2.5 h-2.5 rounded-full bg-[#00D26A] inline-block" />;

export default function OperationsTab({ tid }) {
  const [data, setData] = useState(null);

  const load = () => api.get(`/tournaments/${tid}/operations`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); /* eslint-disable-next-line */ }, [tid]);

  if (!data) return <p className="text-center text-gray-600 py-10 text-sm">Loading operations…</p>;
  const c = data.counts;
  const rev = data.revenue || { total: 0, currency: 'gbp', payments: 0, paid_entries: 0 };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest flex items-center gap-2"><Activity size={14} className="text-[#00D26A]" /> Live Operations</p>
        <button onClick={load} className="text-gray-500 hover:text-white"><RefreshCw size={15} /></button>
      </div>

      <div className="bg-gradient-to-br from-[#00D26A]/15 to-[#1C1C1E] border border-[#00D26A]/40 rounded-2xl p-5" data-testid="ops-revenue">
        <div className="flex items-center gap-2 mb-1 text-[#00D26A]"><Banknote size={16} /><span className="text-[10px] uppercase font-bold tracking-widest">Total collected (private)</span></div>
        <p className="text-4xl font-black" data-testid="ops-revenue-total">{CUR[rev.currency] || ''}{rev.total.toLocaleString()}</p>
        <p className="text-xs text-gray-500 mt-1">{rev.payments} online payment{rev.payments === 1 ? '' : 's'} · {rev.paid_entries} paid entries</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Tile icon={<Goal size={15} />} label="Goals" value={c.goals} />
        <Tile icon={<Clock size={15} />} label="Upcoming" value={c.upcoming} accent="#FFD400" />
        <Tile icon={<CheckCircle2 size={15} />} label="Completed" value={`${c.completed}/${c.fixtures}`} accent="#00D26A" />
        <Tile icon={<Users size={15} />} label="Officials In" value={`${c.checked_in}/${c.officials}`} />
      </div>

      <div className="bg-[#1C1C1E] border border-gray-800 rounded-2xl p-4 flex justify-around text-center">
        <div><p className="text-2xl font-black text-[#FFD400]">{c.yellow}</p><p className="text-[9px] text-gray-500 uppercase tracking-wider">Yellow</p></div>
        <div><p className="text-2xl font-black text-[#FF3B30]">{c.red}</p><p className="text-[9px] text-gray-500 uppercase tracking-wider">Red</p></div>
        <div><p className="text-2xl font-black text-white">{c.goals}</p><p className="text-[9px] text-gray-500 uppercase tracking-wider">Goals</p></div>
      </div>

      {data.upcoming.length > 0 && (
        <div>
          <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest mb-2">Upcoming Kickoffs</p>
          <div className="space-y-2">
            {data.upcoming.slice(0, 6).map((u) => (
              <div key={u.id} className="bg-[#1C1C1E] border border-gray-800 rounded-xl px-4 py-2.5 flex items-center justify-between">
                <span className="text-sm font-bold">{u.label}</span>
                <span className="text-[10px] text-gray-500 uppercase">{u.stage} R{u.round}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest mb-2">Timeline Feed</p>
        {data.timeline.length === 0 ? <p className="text-center text-gray-600 py-6 text-sm">No events yet.</p> :
          <div className="space-y-2">
            {data.timeline.map((e, i) => (
              <div key={i} className="bg-[#1C1C1E] border border-gray-800 rounded-xl px-4 py-2.5 flex items-center gap-3">
                <span className="text-[#FFD400] font-mono text-xs font-bold w-10">{e.minute}</span>
                {eventDot(e.type)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{e.type === 'goal' ? 'Goal' : e.type === 'yellow' ? 'Yellow' : e.type === 'red' ? 'Red' : e.type} • {e.team}{e.player ? ` #${e.player}` : ''}</p>
                  <p className="text-[10px] text-gray-500 truncate">{e.fixture}</p>
                </div>
              </div>
            ))}
          </div>}
      </div>
    </div>
  );
}
